import { useState, useMemo, useRef, useEffect } from "react";
import { createPortal } from "react-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
  User, Edit2, X, Search, Phone, Check,
} from "lucide-react";
import { supabase } from "@/lib/supabase";
import type { Database } from "@/lib/database.types";
import { useRequireAuth, hasRole } from "@/lib/auth";
import { Card, Spinner } from "@/components/ui";

// =============================================================================
// ProjectTeamCard — T-146.
//
// Replaces the old read-only "Team" card on ProjectDetailScreen. Each of the
// three project-level slots (Project Manager / Supervisor / Site Lead) is now
// a click-to-edit picker so manager+ can swap the assignment from the project
// header without opening a separate edit dialog.
//
// Always renders for managers (so a blank project can be staffed up from this
// card) and renders only when at least one slot is set for non-managers.
//
// Saves directly to public.projects.{project_manager_id, supervisor_id,
// site_lead_id} via supabase update. RLS already enforces manager+.
// =============================================================================

interface EmbeddedEmployee {
  employee_id:        string;
  first_name:         string;
  last_name:          string;
  primary_role_name:  string | null;
  mobile:             string | null;
}

export interface Props {
  project: {
    project_id:          string;
    project_manager_id:  string | null;
    supervisor_id:       string | null;
    site_lead_id:        string | null;
    project_manager?:    EmbeddedEmployee | null;
    supervisor?:         EmbeddedEmployee | null;
    site_lead?:          EmbeddedEmployee | null;
  };
  onSaved?: () => void;
}

type SlotKey = "project_manager_id" | "supervisor_id" | "site_lead_id";

const SLOTS: { key: SlotKey; label: string; embedKey: "project_manager" | "supervisor" | "site_lead" }[] = [
  { key: "project_manager_id", label: "Project manager", embedKey: "project_manager" },
  { key: "supervisor_id",      label: "Supervisor",      embedKey: "supervisor" },
  { key: "site_lead_id",       label: "Site lead",       embedKey: "site_lead" },
];

export default function ProjectTeamCard({ project, onSaved }: Props) {
  const user = useRequireAuth();
  const canEdit = hasRole(user, "manager");

  const anyAssigned =
    !!project.project_manager_id ||
    !!project.supervisor_id ||
    !!project.site_lead_id;

  // Hide entirely for non-managers when nothing's set — matches the old behaviour.
  if (!canEdit && !anyAssigned) return null;

  return (
    <Card>
      <div className="p-5">
        <div className="flex items-center gap-2 mb-3 flex-wrap">
          <User size={14} className="text-text-muted"/>
          <span className="text-[12px] uppercase tracking-wider text-text-muted">Team</span>
          <span className="flex-1"/>
          {/* 3 Nov 2026 (mig 495) — James: "access has to be either side … maybe 2 weeks either side with a way to edit it and maybe go 4 weeks on a site we need" */}
          <AccessTail project={project} canEdit={canEdit} onSaved={onSaved}/>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          {SLOTS.map(slot => (
            <RolePicker
              key={slot.key}
              label={slot.label}
              employee={(project[slot.embedKey] ?? null) as EmbeddedEmployee | null}
              canEdit={canEdit}
              onSave={async (employeeId) => {
                // T-162a — dynamic-key update needs explicit Update-shape narrowing;
                // slot.key is a SlotKey union ("project_manager_id" | "supervisor_id" | "site_lead_id"),
                // all three of which exist on projects.Update with type `string | null | undefined`.
                const patch: Database["public"]["Tables"]["projects"]["Update"] = { [slot.key]: employeeId };
                const { error } = await supabase
                  .from("projects")
                  .update(patch)
                  .eq("project_id", project.project_id);
                if (error) throw error;
                onSaved?.();
              }}
            />
          ))}
        </div>
      </div>
    </Card>
  );
}

// =============================================================================
// RolePicker — one slot. Read mode shows employee name + pencil; click opens
// a small inline dropdown with employee search and a "Clear" option.
// =============================================================================
function RolePicker({
  label, employee, canEdit, onSave,
}: {
  label: string;
  employee: EmbeddedEmployee | null;
  canEdit: boolean;
  onSave: (employeeId: string | null) => Promise<void>;
}) {
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const anchorRef = useRef<HTMLDivElement>(null);

  async function handlePick(employeeId: string | null) {
    setSaving(true);
    try {
      await onSave(employeeId);
      setOpen(false);
    } catch (e: any) {
      alert(`Save failed: ${e?.message ?? String(e)}`);
    } finally {
      setSaving(false);
    }
  }

  return (
    <div ref={anchorRef}>
      <div className="text-[10px] uppercase tracking-wider text-text-muted">{label}</div>
      {employee ? (
        <div className="mt-1 flex items-center gap-2">
          <div className="min-w-0">
            <div className="text-[13px] font-medium truncate">
              {employee.first_name} {employee.last_name}
            </div>
            <div className="text-[10px] text-text-muted truncate">
              {employee.primary_role_name ?? "—"}
              {employee.mobile && (
                <a
                  href={`tel:${employee.mobile.replace(/[^\d+]/g, "")}`}
                  className="ml-2 inline-flex items-center gap-0.5 hover:text-accent"
                  title={`Call ${employee.first_name}`}
                >
                  <Phone size={9}/>{employee.mobile}
                </a>
              )}
            </div>
          </div>
          {canEdit && (
            <button
              type="button"
              onClick={() => setOpen(o => !o)}
              className="p-1 text-text-muted hover:text-accent rounded hover:bg-ink"
              title={`Change ${label.toLowerCase()}`}
              disabled={saving}
            >
              {saving ? <Spinner size={11}/> : <Edit2 size={11}/>}
            </button>
          )}
        </div>
      ) : (
        canEdit ? (
          <button
            type="button"
            onClick={() => setOpen(o => !o)}
            className="mt-1 text-[12px] text-accent hover:underline disabled:opacity-50"
            disabled={saving}
          >
            {saving ? <Spinner size={11}/> : "+ Assign"}
          </button>
        ) : (
          <div className="mt-1 text-[12px] text-text-dim">Unassigned</div>
        )
      )}

      {open && canEdit && (
        <EmployeePickerDropdown
          anchorRef={anchorRef}
          currentId={employee?.employee_id ?? null}
          onClose={() => setOpen(false)}
          onPick={handlePick}
        />
      )}
    </div>
  );
}

// =============================================================================
// EmployeePickerDropdown — small floating panel with search and active-only
// employee list. Click an entry → save. Includes "Clear assignment" if the
// slot currently has someone in it.
// =============================================================================
function EmployeePickerDropdown({
  anchorRef, currentId, onClose, onPick,
}: {
  anchorRef: React.RefObject<HTMLDivElement>;
  currentId: string | null;
  onClose: () => void;
  onPick: (employeeId: string | null) => void;
}) {
  const [search, setSearch] = useState("");
  const panelRef = useRef<HTMLDivElement>(null);
  const [pos, setPos] = useState<{ top: number; left: number; width: number } | null>(null);
  const qc = useQueryClient();

  // Reuse the same query key the assignments form uses so we hit one cache.
  const { data: employees, isLoading } = useQuery({
    queryKey: ["employees_for_project_assign"],
    queryFn: async () => {
      const cached = qc.getQueryData<any[]>(["employees_for_project_assign"]);
      if (cached) return cached;
      const { data, error } = await supabase
        .from("employees")
        .select("employee_id, first_name, last_name, primary_role_name, mobile")
        .eq("is_active", true)
        .order("last_name");
      if (error) throw error;
      return data ?? [];
    },
  });

  // Position the panel under the anchor; flip above if no room below.
  useEffect(() => {
    function reposition() {
      const el = anchorRef.current;
      if (!el) return;
      const rect = el.getBoundingClientRect();
      const panelW = Math.max(288, rect.width); // 288 = w-72
      const panelH = 320; // approximate; flips above if below would clip
      const spaceBelow = window.innerHeight - rect.bottom;
      const top = spaceBelow >= panelH || rect.top < panelH
        ? rect.bottom + 4
        : Math.max(8, rect.top - panelH - 4);
      const maxLeft = window.innerWidth - panelW - 8;
      const left = Math.min(rect.left, Math.max(8, maxLeft));
      setPos({ top, left, width: panelW });
    }
    reposition();
    window.addEventListener("scroll", reposition, true);
    window.addEventListener("resize", reposition);
    return () => {
      window.removeEventListener("scroll", reposition, true);
      window.removeEventListener("resize", reposition);
    };
  }, [anchorRef]);

  // Click-outside / escape to close.
  useEffect(() => {
    function onDocClick(e: MouseEvent) {
      const t = e.target as Node;
      if (
        panelRef.current && !panelRef.current.contains(t) &&
        anchorRef.current && !anchorRef.current.contains(t)
      ) onClose();
    }
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") onClose();
    }
    document.addEventListener("mousedown", onDocClick);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDocClick);
      document.removeEventListener("keydown", onKey);
    };
  }, [onClose, anchorRef]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return employees ?? [];
    return (employees ?? []).filter((e: any) => {
      const name = `${e.first_name} ${e.last_name}`.toLowerCase();
      return name.includes(q) || (e.primary_role_name ?? "").toLowerCase().includes(q);
    });
  }, [employees, search]);

  if (!pos) return null;

  return createPortal(
    <div
      ref={panelRef}
      style={{ position: "fixed", top: pos.top, left: pos.left, width: pos.width }}
      className="z-[1000] bg-ink-card border border-line rounded-md shadow-xl"
    >
      <div className="p-2 border-b border-line flex items-center gap-2">
        <Search size={12} className="text-text-muted"/>
        <input
          autoFocus
          value={search}
          onChange={e => setSearch(e.target.value)}
          placeholder="Search employees…"
          className="bg-transparent border-none outline-none text-[13px] flex-1 text-text"
        />
        {search && (
          <button onClick={() => setSearch("")} className="text-text-muted hover:text-text">
            <X size={11}/>
          </button>
        )}
      </div>
      <div className="max-h-72 overflow-y-auto">
        {isLoading && <div className="py-4 flex justify-center"><Spinner size={12}/></div>}
        {!isLoading && filtered.length === 0 && (
          <div className="px-3 py-3 text-[12px] text-text-muted">No matching employees.</div>
        )}
        {filtered.map((e: any) => {
          const isCurrent = e.employee_id === currentId;
          return (
            <button
              key={e.employee_id}
              type="button"
              onClick={() => onPick(e.employee_id)}
              className={`w-full text-left px-3 py-2 hover:bg-ink-hover flex items-center justify-between gap-2 ${
                isCurrent ? "bg-accent/10" : ""
              }`}
            >
              <div className="min-w-0">
                <div className="text-[13px] truncate text-text">
                  {e.last_name}, {e.first_name}
                </div>
                {e.primary_role_name && (
                  <div className="text-[10px] text-text-muted truncate">{e.primary_role_name}</div>
                )}
              </div>
              {isCurrent && <Check size={12} className="text-accent flex-shrink-0"/>}
            </button>
          );
        })}
      </div>
      {currentId && (
        <button
          type="button"
          onClick={() => onPick(null)}
          className="w-full text-left px-3 py-2 border-t border-line text-[12px] text-lost hover:bg-lost/10 inline-flex items-center gap-1.5"
        >
          <X size={11}/> Clear assignment
        </button>
      )}
    </div>,
    document.body,
  );
}

// -----------------------------------------------------------------------------
// mig 495 — how long after their last day on site the crew keep this job
// (diary, documents, site details, HOP). Before the start they have it from the
// moment they are rostered. Default 14 days; up to 60.
// -----------------------------------------------------------------------------
function AccessTail({ project, canEdit, onSaved }: { project: any; canEdit: boolean; onSaved?: () => void }) {
  const current: number = Number((project as any).access_tail_days ?? 14);
  const [busy, setBusy] = useState(false);
  async function set(days: number) {
    setBusy(true);
    const { error } = await (supabase as any).from("projects").update({ access_tail_days: days }).eq("project_id", project.project_id);
    setBusy(false);
    if (error) { window.alert(`Could not change it: ${error.message}`); return; }
    onSaved?.();
  }
  const opts = [7, 14, 28, 42, 60];
  return (
    <span className="inline-flex items-center gap-1.5 text-[11px] text-text-muted" title="How long after their last day on site the crew keep this job's diary, documents and site details. They have it from the moment they are rostered on.">
      <span>Site access after the job:</span>
      {canEdit ? (
        <select value={opts.includes(current) ? current : 14} disabled={busy} onChange={e => set(Number(e.target.value))} className="bg-ink border border-line rounded px-1.5 py-0.5 text-[11px] text-text">
          {opts.map(d => <option key={d} value={d}>{d === 7 ? "1 week" : d === 14 ? "2 weeks" : d === 28 ? "4 weeks" : d === 42 ? "6 weeks" : "60 days"}</option>)}
        </select>
      ) : <span className="font-mono text-text">{current} d</span>}
    </span>
  );
}
